import { getDeviceType } from "@b2bportal/core-utilities";
import {
  type AvailabilityRequest,
  AvailabilityRequestEnum,
  LodgingCollection,
  LodgingSelectionEnum,
  Platform,
  type ShopResponse,
  type ShopResponseAvailable,
  ShopResponseEnum,
  type LodgingTravelWalletOffer,
} from "@b2bportal/lodging-api";
import { hotelsApi } from "@overrides/utilities";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { HotelsShopActions, type ShopSearch } from "..";
import type { HotelsState } from "../../../../store";
import { availabilityTokenCache, shopTokenCache } from "~core-hotels/utilities";

export type FetchShopFulfillResponse = {
  shop: ShopResponseAvailable;
  bestOffer?: LodgingTravelWalletOffer;
  search: ShopSearch;
  availabilityTrackingProperties?: Record<string, any>;
};

export const fetchHotelsShop = createAsyncThunk<
  FetchShopFulfillResponse,
  { search: ShopSearch },
  { state: HotelsState; rejectValue: { error: string } }
>("hotelsShop/fetchHotelsShop", async ({ search }, thunkAPI) => {
  const { isDesktopAndUp } = getDeviceType();

  const availabilityRequest: AvailabilityRequest = {
    progressiveConfig: {},
    stayDates: search.dateRange,
    guests: search.guests,
    platform: isDesktopAndUp ? Platform.Desktop : Platform.Mobile,
    lodgingSelection: {
      lodgingIds: [search.lodgingId],
      preserveOrder: true,
      LodgingSelection: LodgingSelectionEnum.LodgingIds,
    },
    rooms: {
      numberOfRooms: search.rooms ?? 1,
    },
    lodgingCollection: LodgingCollection.NoCollection,
    excludeNonLuxuryLodgings: false,
    AvailabilityRequest: AvailabilityRequestEnum.InitialSearch,
  };

  try {
    const availabilityRes =
      await hotelsApi.apiV0HotelsAvailabilityPost(availabilityRequest);

    const lodging =
      availabilityRes.data.lodgings.length > 0
        ? availabilityRes.data.lodgings[0]
        : null;

    if (lodging != null) {
      thunkAPI.dispatch(HotelsShopActions.setLodging(lodging));
    }

    if (lodging?.price == null) {
      thunkAPI.dispatch(HotelsShopActions.setLodgingUnavailable(true));
      return thunkAPI.rejectWithValue({
        error: "Lodging not available",
      });
    }

    const cacheKey = {
      lodgingId: search.lodgingId,
      fromDate: search.dateRange.from,
      untilDate: search.dateRange.until,
      guests: search.guests,
      rooms: search.rooms,
    };
    const opaqueRequest =
      shopTokenCache.get(cacheKey) ??
      availabilityTokenCache.get(cacheKey) ??
      lodging.price?.opaqueShopRequest;

    if (opaqueRequest == null) {
      return thunkAPI.rejectWithValue({
        error: "Opaque shop request not available",
      });
    }

    shopTokenCache.set(cacheKey, opaqueRequest);

    let data: ShopResponse;
    try {
      const res = await hotelsApi.apiV0HotelsShopPost({ opaqueRequest });
      data = res.data;
    } catch {
      const opaqueRequest = lodging.price.opaqueShopRequest;
      // If the initial shop request fails, try to use the `opaqueShopRequest`
      // from the availability response.
      const res = await hotelsApi.apiV0HotelsShopPost({ opaqueRequest });
      shopTokenCache.set(cacheKey, opaqueRequest);
      data = res.data;
    }

    switch (data.ShopResponse) {
      case ShopResponseEnum.Available: {
        const bestOffer =
          availabilityRes.data.offers?.offersByProductId[search.lodgingId];
        const availabilityTrackingProperties =
          availabilityRes.data.trackingPropertiesV2;
        return {
          shop: data,
          search,
          bestOffer,
          availabilityTrackingProperties,
        };
      }
      case ShopResponseEnum.Failure:
        return thunkAPI.rejectWithValue({ error: ShopResponseEnum.Failure });
      case ShopResponseEnum.Unavailable:
        return thunkAPI.rejectWithValue({
          error: ShopResponseEnum.Unavailable,
        });
    }
  } catch (error) {
    // Reject the thunk with an error message
    return thunkAPI.rejectWithValue({ error: String(error) });
  }
});
